import * as d3 from 'd3'

import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { GlobalCfgFactory } from '@shared/factories/global-cfg/global-cfg.factory';
import { CommonUtilsService } from '@shared/services/common-utils/common-utils.service';
import { TooltipModule } from 'primeng/tooltip';
import { ExportServiceService } from '@shared/services/export-service/export-service.service';
import { LoaderFactory } from '@shared/factories/loader/loader.factory';
import { AfTranslatePipe } from '@shared/modules/translate/pipe/af-translate.pipe';
import { DownloadService } from '@shared/services/download/download.service';
import { ModalServiceService } from '@shared/services/modal-service/modal-service.service';
import { AfTranslateFactory } from '@shared/modules/translate/factory/translate.factory';

@Component({
  selector: 'organization-chart',
  templateUrl: './organization-chart.component.html',
  standalone: true,
  imports: [
    TooltipModule,
    AfTranslatePipe
  ]
})
export class OrganizationChartComponent implements OnChanges {
  skin: string = this.globalCfg.skin;
  organizationChart: any = {
    graphPathColor: '#B4B3B3',
    graph: {
      width: 1900,
      height: 1000,
      scale: 1,
      margin: {
        top: 20,
        right: 90,
        bottom: 30,
        left: 90
      },
      nodeW: 105,
      nodeH: 20,
      spaceX: 125,
      spaceY: 20,
      i: 0,
      x: 1900/2,
      y: 1000/2,
      zoomScale: 1
    }
  }

  constructor(private globalCfg: GlobalCfgFactory,
    private loader: LoaderFactory,
    private translate: AfTranslateFactory,
    private commonUtilsService: CommonUtilsService,
    private downloadService: DownloadService,
    private exportService: ExportServiceService,
    private modalService: ModalServiceService) { }

  @Input() client: any = null;
  @Input() filials: any = null;
  @Output() goBack = new EventEmitter();

  ngOnChanges(): void {
    if(this.filials){
      this.initGraph();
    }
  }

  initGraph(){
    let graph:any = this.organizationChart.graph
    graph.svg = d3.select(".organization-chart__block")
      .append("svg")
      .attr("width", graph.width)
      .attr("height", graph.height);

    // Define defs with an arrow marker for the paths
    graph.svg.append("defs")
      .append("marker")
      .attr("id", "graph_arrow")
      .attr("viewBox", "0 0 8 8")
      .attr("refX", "4")
      .attr("refY", "4")
      .attr("markerWidth", "8")
      .attr("markerHeight", "8")
      .attr("orient", "auto-start-reverse")
      .append("path")
      .attr("fill", "none")
      .attr("stroke-width", "1")
      .attr("stroke", this.organizationChart.graphPathColor)
      .attr("d", "M 0 0 L 4 4 L 0 8");

    graph.g = graph.svg.append('g')
      .attr('class', 'organization-chart__graph-container')
      .attr('id', 'organization-chart-'+ this.client.clientId)
      .attr('transform', `translate(${graph.x}, ${graph.y}), scale(${graph.zoomScale})`);

    graph.zoom = d3.zoom()
      .scaleExtent([0.3, 3])
      .on("zoom", this.zoomWithButton);


    graph.svg.call(graph.zoom)
      .on("wheel.zoom", null)
      .on("mousedown.zoom", null)
      .on("dblclick.zoom", null)

    // declares a tree layout and assigns the size
    graph.treemap = d3.tree().nodeSize([graph.nodeW + graph.spaceX, graph.nodeH + graph.spaceY]);

    // Assigns parent, children, height, depth
    graph.root = d3.hierarchy(this.filials, (d:any)=> { return d.children });
    graph.root.x0 = graph.height / 2;
    graph.root.y0 = 0;

    // Collapse after the second level
    // graph.root.children.forEach(_collapse);
    this.update(graph.root, false, true);
    setTimeout(() => {
      this.centerGraph();
    });
  }

  zoomWithButton(type: any) {
    let graph: any = this.organizationChart.graph;
    if (type === 'increment' && graph.zoomScale < 3) {
      graph.zoomScale += 0.3
    } else if (type === 'decrement' && graph.zoomScale > 0.4) {
      graph.zoomScale -= 0.3
    }

    graph.x = graph.width / 2;
    graph.y = graph.height * .16
    graph.g.attr("transform", `translate(${graph.x}, ${graph.y}), scale(${graph.zoomScale})`);

    const gElement: any = document.querySelector('svg g')?.getBoundingClientRect();
    let graphWidthUpdate = this.organizationChart.graph.width;
    let graphHeightUpdate = this.organizationChart.graph.height;
    if (gElement && (gElement.width > graph.width || gElement.height > graph.height)) {
      graphWidthUpdate = gElement.width + 100;
      graphHeightUpdate = gElement.height + 100;
    }
    graph.svg.attr('width', graphWidthUpdate).attr('height', graphHeightUpdate);
    graph.x = graphWidthUpdate / 2;
    graph.y = graphHeightUpdate * .16
    graph.g.attr("transform", `translate(${graph.x}, ${graph.y}), scale(${graph.zoomScale})`);

    this.update(graph.nodes, false, false);
  }

  update(source: any, centerMySelf: any = false, init: any = false) {
    let that: any = this.organizationChart
    let treeData : any = that.graph.treemap(that.graph.root);
    that.graph.nodes = treeData.descendants(),
    that.graph.links = treeData.descendants().slice(1);
    that.graph.nodes.forEach((d: any) => {
      d.y = d.depth * 92;
      d.x = d.x * 1.15;
    });
    let node: any = that.graph.g
      .selectAll('.node')
      .data(that.graph.nodes, (d: any) => {
        return d.id || (d.id = ++that.graph.i)
      });

    let nodeEnter: any = node.enter()
      .append('g')
      .attr('class', 'node')
      .attr('transform', (d: any) => {
        return `translate(${d.x - that.graph.nodeW}, ${d.y - that.graph.nodeH})`;
      })
      .attr('fill', '#F4F4F4')
      .on('click',(ev:any, d:any)=>{
        this.clickNode(d, this)
      })
      // .on('click', (d: any) => {
      //   this.clickNode(d, this);
      // })
      .append('foreignObject')
      .attr('class', 'organization-chart__node-block')
      .append('xhtml:div')
      .attr('class', (d: any)=>{
        return `common-diagram-card__expand-box-container ${d.data.clientId === this.client.clientId ? 'common-diagram-card__expand-box-container--selected': ''}`;
      })
      .html((d:any) => {
        return `<div class="common-diagram-card__expand-box-icon-container">` +
          (d.data.children.length
            ? `<i class="${d.collapse ? 'lf-icon-expand-vertical' : 'lf-icon-collapse'} common-icon__sm-clickable node-icon-${d.data.clientId}"></i>`
            : '') +
        `</div>
        <div class="common-diagram-card__expand-box-name-percent-container">
          <p id="node-text${d.data.clientId}" class="common-diagram-card__expand-box-name-text">${d.data.name}</p>` +
          (d.data.percentage && d.data.percentage !== null
            ? `<p class="common-diagram-card__expand-box-percent-text">${this.commonUtilsService.setLocaleNumberFormat('' + d.data.percentage)} %</p>`
            : '') +
        `</div>`;
      });

    let nodeUpdate: any = nodeEnter.merge(node);
    nodeUpdate.transition()
      .duration(750)
      .attr("transform", (d: any) => {
        return `translate(${d.x - that.graph.nodeW}, ${d.y - that.graph.nodeH})`;
      });

    node.exit()
      .transition()
      .duration(750)
      .attr("transform", (d: any) => {
        return `translate(${source.x - that.graph.nodeW}, ${source.y - that.graph.nodeH})`;
      })
      .remove();

    let link: any = that.graph.g.selectAll('path.link')
      .data(that.graph.links, (d: any) => {
        return d.id;
      });

    let linkEnter: any = link.enter()
      .insert('path', "g")
      .attr("class", "link")
      .attr('class','link')
      .attr("fill", "none")
      .attr("stroke-width", 1)
      .attr('stroke', this.organizationChart.graphPathColor)
      .attr('d', (d: any) => {
        let o: any = {
          x: source.x0,
          y: source.y0
        }
        return this.diagonal(o, o)
      })
      .attr("marker-start", "url(#graph_arrow)");

    let linkUpdate: any = linkEnter.merge(link);
    linkUpdate
      .transition()
      .duration(750)
      .attr('d', (d: any) => {
        return this.diagonal(d, d.parent)
      });

    link.exit()
      .transition()
      .duration(750)
      .attr('d', (d: any) => {
        let o: any = {
          x: source.x,
          y: source.y
        }
        return this.diagonal(o, o);
      })
      .remove();


    that.graph.nodes.forEach((d: any) => {
      d.x0 = d.x;
      d.y0 = d.y;
    });

    if (centerMySelf) {
      let x: any;
      let y: any;

      that.graph.nodes.forEach((d: any) => {
        if (d.data.clientId === this.client.clientId) {
          x = d.x;
          y = d.y;
        }
      });

      // normalize for width/height
      let new_x: any = (-x + ( that.graph.x));
      let new_y: any = (-y + ( that.graph.height * .26));

      // move the main container g
      that.graph.zoomScale = 0.6;
      that.graph.g.attr("transform", `translate(${new_x}, ${new_y}), scale(${that.graph.zoomScale})`);
      setTimeout(function() {
        $('.organization-chart__block')[0].scrollTop = 0;
        $('.organization-chart__block')[0].scrollLeft = 0
      });
    }
    if (init) {
      this.reduceGraphZoom();
    }
  }

  reduceGraphZoom(){
    let d3Select: any = d3.select('.organization-chart__block');
    if (d3Select.select('svg').node().getBBox().width > 878 || d3Select.select('svg').node().getBBox().height > 459) {
      this.organizationChart.graph.scale -= 0.1;
      this.organizationChart.graph.zoomScale = this.organizationChart.graph.scale;
      this.organizationChart.graph.g.attr("transform", `translate(${this.organizationChart.graph.x}), scale(${this.organizationChart.graph.zoomScale})`);
      this.reduceGraphZoom();
    }
  }

  diagonal(s: any, d: any) {
    const direction = s.x > d.x ? -1 : 1;
    const yMultiplier = ((s.y + d.y) / 2) + 9.5;
    if (s.x === d.x) {
      return `M ${s.x} ${s.y - 23} L ${d.x} ${d.y + 42}`;
    } else {
      return `M ${s.x} ${s.y - 23} Q ${s.x} ${yMultiplier} ${s.x + (16 * direction)} ${yMultiplier} L ${d.x - (10 * direction)} ${yMultiplier} Q ${d.x} ${yMultiplier} ${d.x} ${d.y + 42}`;
    }
  }

  clickNode(d: any, thisComponent: any) {
    if (d.children) {
      d._children = d.children;
      d.collapse = true;
      d.children = null;
    } else {
      d.children = d._children;
      d.collapse = false;
      d._children = null;
    }
    $(`.node-icon-${d.data.clientId}`).toggleClass('lf-icon-expand-vertical lf-icon-collapse');
    thisComponent.update(d);
  }

  centerGraph(){
    let that: any = this.organizationChart;
    that.graph.width = 1900;
    that.graph.height = 1000;
    that.graph.x = that.graph.width / 3;
    that.graph.y = that.graph.height * .16
    that.graph.zoomScale = that.graph.scale;
    that.graph.g.attr("transform", `translate(${that.graph.x}, ${that.graph.y}), scale(${that.graph.zoomScale})`)
      .transition()
      .duration(750);
    this.update(that.graph.root);
    setTimeout(function() {
      $('.organization-chart__block')[0].scrollTop = 0;
      $('.organization-chart__block')[0].scrollLeft = 0
    });
  }

  showMySelf() {
    this.organizationChart.graph.root.children.forEach((child: any) => {
      this.expand(child, this);
    });
    this.update(this.organizationChart.graph.root, true);
  }

  expandAll(){
    let that: any = this.organizationChart
    if (that.graph.root._children) {
      that.graph.root.children = that.graph.root._children
      that.graph.root.collapse = false;
      that.graph.root._children = null
      if ($(`.node-icon-${that.graph.root.data.clientId}`).hasClass('lf-icon-expand-vertical')) {
        $(`.node-icon-${that.graph.root.data.clientId}`).toggleClass('lf-icon-expand-vertical lf-icon-collapse');
      }
    }

    if (that.graph.root.children) {
      that.graph.root.children.forEach((child: any) => {
        this.expand(child, this);
      });
      this.update(that.graph.root);
    }
  }

  expand(d: any, that: any) {
    if (d._children) {
      d.children = d._children
      d.collapse = false;
      d._children = null
      if ($(`.node-icon-${d.data.clientId}`).hasClass('lf-icon-expand-vertical')) {
        $(`.node-icon-${d.data.clientId}`).toggleClass('lf-icon-expand-vertical lf-icon-collapse');
      }
    }
    if (d.children) {
      d.children.forEach((child: any) => {
        that.expand(child, that)
      });
    }
  }

  exportToImg(){
    let that: any = this.organizationChart;
    let serializer: any = new XMLSerializer();
    let svgElement : any = serializer.serializeToString(that.graph.svg.node());
    let widthScale: number = Math.ceil(that.graph.g.node().getBoundingClientRect().width/that.graph.zoomScale)+10;
    let heightScale: number = Math.ceil(that.graph.g.node().getBoundingClientRect().height/that.graph.zoomScale)+10;
    let positionGElement: number = svgElement.indexOf('<g class="organization-chart__graph-container" id="organization-chart-'+this.client.clientId);
    let endPositionGElement: number = svgElement.indexOf('>', positionGElement)+1;
    let gElement: string = svgElement.slice(positionGElement, endPositionGElement);
    let gNewElement: string = '<g class="organization-chart__graph-container" id="organization-chart-'+this.client.clientId+'" transform="translate('+(widthScale/2)+', 25), scale(1)">'
    let htmlPrint: any = svgElement.replace(gElement, gNewElement);
    htmlPrint = htmlPrint.replace('width="'+that.graph.width+'" height="'+that.graph.height+'"', 'width="'+widthScale+'" height="'+heightScale+'"');

    let config = {
      htmlPrint: htmlPrint,
      width: widthScale,
      height: heightScale,
      name: this.client.name.replace(/ /g, ""),
      successCallback: (uploadResponse:any, blob:any) => {
        let dataBlob = new Blob([blob], { type: 'image/png' });
        this.downloadService.triggerDownload(dataBlob, uploadResponse.fileName);
        // window.open(URL.createObjectURL(blob), '_blank');
        this.loader.closeLoader(['export-file']);
        this.modalService.successModal({ text: this.translate.instant('organization-chart.export-to-img')});
      }
    }
    this.exportService.convertToImg(config);
  }
}
